// src/App.js
// ─── Orchestrateur principal HmongHearts ───
// Ce fichier gère uniquement : routing, auth state, et layout global.

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Loader } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';

// Services & Hooks
import { useTranslation } from 'react-i18next';
import userService from './services/userService';
import { initAnalytics, trackPageView } from './services/analyticsService';
import { isProfileOnboardingComplete, canAccessDating } from './utils';
import { useToast, useDarkMode } from './hooks';

// Layout
import Nav from './components/Nav';
import CookieBanner from './components/CookieBanner';
import ChatWidget from './components/ChatWidget';
import { Toasts } from './components/ui';

// Pages
import HomePage from './pages/HomePage';
import DiscoverPage from './pages/DiscoverPage';
import ProfilePage from './pages/ProfilePage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import MatchesPage from './pages/MatchesPage';
import MessagesPage from './pages/MessagesPage';
import ConversationPage from './pages/ConversationPage';
import NotificationsPage from './pages/NotificationsPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import ConfidentialitePage from './pages/ConfidentialitePage';
import CguPage from './pages/CguPage';
import AuthActionPage from './pages/AuthActionPage';
import OnboardingPage from './pages/OnboardingPage';
import AdminPage from './pages/AdminPage';
import AnnoncesPage from './pages/AnnoncesPage';
import AnnonceDetailPage from './pages/AnnonceDetailPage';
import NotFoundPage from './pages/NotFoundPage';
import FeedbackPage from './pages/FeedbackPage';
import MembersPage from './pages/MembersPage';
import SettingsPage from './pages/SettingsPage';

// Layout
import ErrorBoundary from './components/ErrorBoundary';

// Modals
import AuthModal from './modals/AuthModal';

// Scroll en haut à chaque changement de route
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// ── Composant interne (a accès au router) ──
function AppContent() {
  const [user, setUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const { t: toasts, add: toast } = useToast();
  const { dark, toggle: toggleDark } = useDarkMode();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('hmh-sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('hmh-sidebar-collapsed', String(next));
      } catch {
        // localStorage non disponible
      }
      return next;
    });
  };
  const navigate = useNavigate();
  const location = useLocation();

  // Mise à jour de l'attribut lang du document
  useEffect(() => {
    document.documentElement.lang = i18n.language.startsWith('en') ? 'en' : 'fr';
  }, [i18n.language]);

  // Init GA4 si consentement déjà accordé
  useEffect(() => {
    initAnalytics();
  }, []);

  // Track changements de page (SPA)
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Auth listener Firebase
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // Bloquer l'accès si l'email n'est pas vérifié
        if (!u.emailVerified) {
          setUser(null);
          setLoading(false);
          return;
        }
        const data = await userService.getUser();
        setUser(data);
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Redirige vers login si page protégée + non connecté
  // Redirige vers /onboarding si onboarding non complété
  useEffect(() => {
    if (loading) return;

    const protectedPaths = ['/profile', '/messages', '/discover', '/settings'];
    if (!user && protectedPaths.includes(location.pathname)) {
      setAuthOpen(true);
    }

    if (user && !isProfileOnboardingComplete(user) && location.pathname !== '/onboarding') {
      navigate('/onboarding');
    }

    const datingPaths = ['/discover', '/matches'];
    if (user && datingPaths.includes(location.pathname) && !canAccessDating(user)) {
      navigate('/');
    }

    if (user && isProfileOnboardingComplete(user) && location.pathname === '/onboarding') {
      navigate('/');
    }
  }, [location.pathname, user, loading, navigate]);

  const handleLogin = (u) => {
    setUser(u);
    if (!isProfileOnboardingComplete(u)) {
      navigate('/onboarding');
    } else {
      navigate('/');
      toast(t('common.welcomeToast'), 'success');
    }
  };

  const handleLogout = async () => {
    await userService.logout();
    setUser(null);
    navigate('/');
    toast(t('common.logoutToast'), 'success');
  };

  // Écran de chargement initial
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-rose-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        <div className="text-center">
          <Loader className="w-12 h-12 text-rose-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 font-semibold">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const isOnboarding = location.pathname === '/onboarding';
  const isInConversation = /^\/messages\/.+/.test(location.pathname);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <ScrollToTop />
      {!isOnboarding && (
        <Nav
          user={user}
          openAuth={() => setAuthOpen(true)}
          dark={dark}
          toggleDark={toggleDark}
          onLogout={handleLogout}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      )}

      <ErrorBoundary
        title={t('errorBoundary.title')}
        desc={t('errorBoundary.desc')}
        reloadLabel={t('errorBoundary.reload')}
      >
        <main
          className={
            user && !isOnboarding
              ? sidebarCollapsed
                ? 'lg:ml-16 transition-[margin] duration-200'
                : 'lg:ml-64 transition-[margin] duration-200'
              : ''
          }
        >
          <Routes>
            <Route
              path="/"
              element={
                <HomePage
                  user={user}
                  openAuth={() => setAuthOpen(true)}
                  onUpdate={setUser}
                  toast={toast}
                />
              }
            />
            <Route
              path="/discover"
              element={
                <DiscoverPage
                  user={user}
                  openAuth={() => setAuthOpen(true)}
                  toast={toast}
                  onUpdate={setUser}
                />
              }
            />
            <Route
              path="/profile"
              element={user ? <ProfilePage user={user} onUpdate={setUser} toast={toast} /> : null}
            />
            <Route
              path="/matches"
              element={
                <MatchesPage
                  user={user}
                  openAuth={() => setAuthOpen(true)}
                  toast={toast}
                  onUpdate={setUser}
                />
              }
            />
            <Route
              path="/notifications"
              element={user ? <NotificationsPage user={user} /> : null}
            />
            <Route
              path="/messages"
              element={
                <MessagesPage user={user} openAuth={() => setAuthOpen(true)} toast={toast} />
              }
            />
            <Route
              path="/messages/:conversationId"
              element={
                <ConversationPage user={user} toast={toast} sidebarCollapsed={sidebarCollapsed} />
              }
            />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/confidentialite" element={<ConfidentialitePage />} />
            <Route path="/cgu" element={<CguPage />} />
            <Route path="/auth/action" element={<AuthActionPage />} />
            <Route
              path="/events"
              element={<EventsPage user={user} openAuth={() => setAuthOpen(true)} toast={toast} />}
            />
            <Route
              path="/events/:id"
              element={
                <EventDetailPage user={user} openAuth={() => setAuthOpen(true)} toast={toast} />
              }
            />
            <Route
              path="/annonces"
              element={
                <AnnoncesPage user={user} openAuth={() => setAuthOpen(true)} toast={toast} />
              }
            />
            <Route
              path="/annonces/:id"
              element={
                <AnnonceDetailPage user={user} openAuth={() => setAuthOpen(true)} toast={toast} />
              }
            />
            <Route
              path="/members"
              element={user ? <MembersPage user={user} toast={toast} onUpdate={setUser} /> : null}
            />
            <Route
              path="/settings"
              element={
                user ? (
                  <SettingsPage
                    user={user}
                    onLogout={handleLogout}
                    dark={dark}
                    toggleDark={toggleDark}
                    toast={toast}
                  />
                ) : null
              }
            />
            <Route path="/admin" element={user ? <AdminPage user={user} toast={toast} /> : null} />
            <Route
              path="/feedback"
              element={user ? <FeedbackPage user={user} toast={toast} /> : null}
            />
            <Route
              path="/onboarding"
              element={
                user && !isProfileOnboardingComplete(user) ? (
                  <OnboardingPage user={user} onUpdate={setUser} toast={toast} />
                ) : null
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
      </ErrorBoundary>

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onLogin={handleLogin}
        toast={toast}
      />
      {!isInConversation && <ChatWidget user={user} />}
      <Toasts toasts={toasts} />
      <CookieBanner user={user} />

      {/* Animations globales */}
      <style>{`
        @keyframes slide-up{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
        @keyframes float-delayed{0%,100%{transform:translateY(0)}50%{transform:translateY(-15px)}}
        @keyframes gradient-shift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
        @keyframes fade-in{from{opacity:0}to{opacity:1}}
        @keyframes fade-in-up{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes like-bounce{0%{transform:scale(1)}40%{transform:scale(1.3)}100%{transform:scale(1)}}
        @keyframes toast-out{from{opacity:1;transform:translateY(0)}to{opacity:0;transform:translateY(-10px)}}
        @keyframes confetti{0%{opacity:1;transform:translate(0,0) scale(1)}100%{opacity:0;transform:translate(var(--cx),var(--cy)) scale(0)}}
        .animate-slide-up{animation:slide-up .3s ease-out}
        .animate-float{animation:float 5s ease-in-out infinite}
        .animate-float-delayed{animation:float-delayed 6s ease-in-out infinite .5s}
        .animate-fade-in{animation:fade-in .2s ease-out}
        .animate-fade-in-up{animation:fade-in-up .3s ease-out both}
        .animate-like-bounce{animation:like-bounce .3s ease-out}
        .animate-toast-out{animation:toast-out .2s ease-out forwards}
        .animate-confetti{animation:confetti .8s ease-out forwards}
        .pb-safe{padding-bottom:env(safe-area-inset-bottom,0px)}
        @media(prefers-reduced-motion:reduce){.animate-slide-up,.animate-fade-in,.animate-fade-in-up,.animate-like-bounce,.animate-toast-out,.animate-confetti,.animate-float,.animate-float-delayed{animation:none!important}*{transition-duration:.01ms!important}}
      `}</style>
    </div>
  );
}

// ── Export avec BrowserRouter ──
export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </HelmetProvider>
  );
}
